/*
 * Open/close behaviour for header dropdowns (category mega menu, account,
 * language): outside click, Escape (focus returns to the trigger) and
 * focus leaving the menu all close it. Owns no markup; the trigger element
 * must carry `data-dropdown-trigger` so Escape can hand focus back.
 */
import { onBeforeUnmount, ref, watch } from 'vue';

export function useDropdown() {
  const root = ref<HTMLElement | null>(null);
  const open = ref(false);

  function close() {
    open.value = false;
  }

  function onPointerDown(e: PointerEvent) {
    if (root.value && !root.value.contains(e.target as Node)) close();
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key !== 'Escape') return;
    close();
    root.value?.querySelector<HTMLElement>('[data-dropdown-trigger]')?.focus();
  }

  function onFocusOut(e: FocusEvent) {
    const next = e.relatedTarget;
    if (next instanceof Node && root.value && !root.value.contains(next)) close();
  }

  function detach() {
    document.removeEventListener('pointerdown', onPointerDown);
    document.removeEventListener('keydown', onKeydown);
  }

  watch(open, (isOpen) => {
    detach();
    if (!isOpen) return;
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeydown);
  });
  onBeforeUnmount(detach);

  return { root, open, close, toggle: () => (open.value = !open.value), onFocusOut };
}
